import {
  ATTACHABLE_STICKY_KINDS,
  CURRENT_SCHEMA_VERSION,
  HOST_STICKY_KINDS,
  validateBoard,
  type BoardConfig,
  type BoardEdge,
  type BoardLevel,
  type BoardElement,
  type Coordinate,
  type DrawingElement,
  type DrawingStrokeStyle,
  type ElementType,
  type EventStormingBoard,
  type NoteAlign,
  type NoteAlignHorizontal,
  type NoteAlignVertical,
  type NoteElement,
} from "@miragon/event-storming-schema-model";
import {
  indexOfOutsideQuotes,
  keywordOf,
  parseAlign,
  parseColor,
  parseCoords,
  parseId,
  parseMultiCoords,
  parseOn,
  parseSize,
  splitAtCoords,
  splitLineComment,
  decodeName,
} from "./lexer.js";
import { IdAllocator, STICKY_ID_PREFIXES, isEngineId } from "./ids.js";

/** Splits `A -> B` at the FIRST arrow — plain indexOf, immune to regex backtracking. */
function splitArrow(core: string): { left: string; right: string } | null {
  const arrow = core.indexOf("->");
  if (arrow <= 0) return null;
  const left = core.slice(0, arrow).trim();
  const right = core.slice(arrow + 2).trim();
  return left && right ? { left, right } : null;
}

const KNOWN_STYLES: ReadonlySet<string> = new Set(["classic", "dark"]);

const ATTACHABLE_KINDS: ReadonlySet<string> = new Set(ATTACHABLE_STICKY_KINDS);

const HOST_KINDS: ReadonlySet<string> = new Set(HOST_STICKY_KINDS);

const KNOWN_LEVELS: ReadonlySet<string> = new Set(["big-picture", "process", "design"]);

// The 8 sticky kinds — the only legal arrow endpoints (mirrors validateBoard's contract).
const STICKY_KINDS: ReadonlySet<string> = new Set(Object.keys(STICKY_ID_PREFIXES));

interface PendingArrow {
  readonly left: string;
  readonly right: string;
  /** Annotation text after `;`. */
  readonly label?: string;
  readonly raw: string;
  readonly lineNo: number;
}

/** `(on …)` attachment — host referenced BY NAME, resolution deferred like arrow endpoints. */
interface PendingAttachment {
  /** Index into `elements` of the attachable sticky. */
  readonly index: number;
  readonly host: string;
  readonly raw: string;
  readonly lineNo: number;
}

function compact<T extends Record<string, unknown>>(obj: T): T {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj)) if (v !== undefined) out[k] = v;
  return out as T;
}

/**
 * Text `(align <h> <v>)` pair -> model value: default axes (left/top) are dropped, both-default
 * collapses to absent — `(align left top)` and no suffix are the same board (the serializer
 * emits the suffix only when an axis differs from the default).
 */
function modelAlign(
  align: { horizontal: NoteAlignHorizontal; vertical: NoteAlignVertical } | undefined,
): NoteAlign | undefined {
  if (!align) return undefined;
  const out = compact({
    horizontal: align.horizontal === "left" ? undefined : align.horizontal,
    vertical: align.vertical === "top" ? undefined : align.vertical,
  }) as NoteAlign;
  return Object.keys(out).length ? out : undefined;
}

/** Finding produced while parsing — line is 1-based; `text` is the (comment-stripped) line. */
export interface ParseDiagnostic {
  readonly line: number;
  readonly message: string;
  readonly text: string;
}

export interface ParseResult {
  readonly board: EventStormingBoard;
  readonly diagnostics: readonly ParseDiagnostic[];
}

/**
 * Line-based keyword parsing; coordinates are `[x, y]` in board pixels (x FIRST), unbounded,
 * optional (default `[0, 0]`). Unknown lines land in `rawPassthrough`.
 */
export function parseDSL(text: string): EventStormingBoard {
  return parseDSLWithDiagnostics(text).board;
}

/**
 * Like `parseDSL`, but additionally returns findings with line numbers (uninterpretable lines,
 * unresolved references) — for editor feedback instead of silent loss.
 */
export function parseDSLWithDiagnostics(text: string): ParseResult {
  const diagnostics: ParseDiagnostic[] = [];
  const ids = new IdAllocator();
  const nameToId = new Map<string, string>();
  const elements: BoardElement[] = [];
  const rawPassthrough: string[] = [];
  const pendingArrows: PendingArrow[] = [];
  const pendingAttachments: PendingAttachment[] = [];

  let config: BoardConfig = { title: "Untitled Board" };
  let inBlockComment = false;

  const register = (name: string, id: string) => {
    if (!nameToId.has(name)) nameToId.set(name, id);
  };

  // Diagnostics helpers: read the current line/number from the loop state.
  let lineNo = 0;
  let currentLine = "";

  /** Tries to capture a line as an arrow. Returns true when consumed. */
  const pushArrow = (line: string): boolean => {
    // Split off an optional arrow annotation after ';' (e.g. `A -> B; async`).
    const semi = line.indexOf(";");
    const core = semi >= 0 ? line.slice(0, semi).trim() : line;
    const arrowLabel = semi >= 0 ? line.slice(semi + 1).trim() : "";
    const arrow = splitArrow(core);
    if (!arrow) return false;
    pendingArrows.push({
      left: arrow.left,
      right: arrow.right,
      ...(arrowLabel ? { label: arrowLabel } : {}),
      raw: line,
      lineNo,
    });
    return true;
  };
  const diag = (message: string, atLine = lineNo, text_ = currentLine) =>
    diagnostics.push({ line: atLine, message, text: text_ });
  /** Passthrough for a line that looks like a known construct but cannot be parsed. */
  const failed = (l: string) => {
    rawPassthrough.push(l);
    diag("Couldn't read this line. It stays in the file unchanged.");
  };
  /** Board pixels, unbounded — no clamping on the free canvas. */
  const pos = (x: number, y: number): Coordinate => ({ x, y });

  const sourceLines = text.split(/\r?\n/);
  for (let i = 0; i < sourceLines.length; i++) {
    const raw = sourceLines[i]!;
    // The empty piece after a final newline is no line: inside an open block comment it would be
    // kept as one, and every save would append another blank line.
    if (raw === "" && i === sourceLines.length - 1) continue;
    lineNo = i + 1;
    currentLine = raw.trim();
    let working = raw;

    // --- Comments (`//`, `/* */`): strip, but keep losslessly in rawPassthrough. ---
    if (inBlockComment) {
      const close = working.indexOf("*/");
      if (close < 0) {
        rawPassthrough.push(raw);
        continue;
      }
      rawPassthrough.push(working.slice(0, close + 2));
      working = working.slice(close + 2);
      inBlockComment = false;
    }
    let open = indexOfOutsideQuotes(working, "/*");
    while (open >= 0) {
      const close = working.indexOf("*/", open + 2);
      if (close < 0) {
        rawPassthrough.push(working.slice(open));
        working = working.slice(0, open);
        inBlockComment = true;
        break;
      }
      rawPassthrough.push(working.slice(open, close + 2));
      working = `${working.slice(0, open)} ${working.slice(close + 2)}`;
      open = indexOfOutsideQuotes(working, "/*");
    }
    const { code, comment } = splitLineComment(working);
    if (comment !== null) rawPassthrough.push(comment);
    working = code;

    const line = working.trim();
    if (!line) continue;

    const kw = keywordOf(line);
    const after = line.slice(kw.length).trim();

    // Arrows FIRST: sticky NAMES may begin with a keyword word (the default command is
    // named "Command" -> arrow `Command -> X`). Declarations carry coordinates `[...]`,
    // arrows never do — but only the part BEFORE the `;` annotation counts, the annotation
    // is free text and may contain a tuple. Without this pre-detection `Command -> X` would
    // be misread as a (broken) `command` declaration and vanish on re-import. `title` is
    // exempt (its content may itself use `->`) UNLESS the left side is an already-declared
    // sticky name (e.g. one named "Title Page" — declarations always precede arrows in
    // serialized DSL). `style`/`level`/`line` need no exemption: style and level values are
    // single words and drawings always carry coordinate tuples.
    const semi = line.indexOf(";");
    const beforeAnnotation = semi >= 0 ? line.slice(0, semi) : line;
    const titleAsArrow = (): boolean => {
      const arrow = splitArrow(beforeAnnotation.trim());
      return arrow !== null && nameToId.has(decodeName(arrow.left));
    };
    if ((kw !== "title" || titleAsArrow()) && !parseCoords(beforeAnnotation) && pushArrow(line)) {
      continue;
    }

    switch (kw) {
      case "title":
        config = { ...config, title: after };
        break;

      case "style": {
        const s = after.toLowerCase();
        if (KNOWN_STYLES.has(s)) config = { ...config, style: s as "classic" | "dark" };
        else failed(line);
        break;
      }

      case "level": {
        const l = after.toLowerCase();
        if (KNOWN_LEVELS.has(l)) config = { ...config, level: l as BoardLevel };
        else failed(line);
        break;
      }

      case "event":
      case "command":
      case "actor":
      case "aggregate":
      case "policy":
      case "readmodel":
      case "external":
      case "hotspot": {
        const node = parseSticky(after);
        if (!node) {
          failed(line);
          break;
        }
        if (node.idInvalid) {
          diag(
            `Couldn't read the id “${node.idInvalid.trim()}”. An id has letters, digits, “_” or “-”, as in (id order_1).`,
          );
        }
        const explicit = node.id !== undefined && ids.claim(node.id) ? node.id : undefined;
        if (node.id !== undefined && explicit === undefined) {
          diag(
            isEngineId(node.id)
              ? `The id “${node.id}” belongs to the board itself, so this sticky got a new id.`
              : `The id “${node.id}” is already taken, so this sticky got a new id.`,
          );
        }
        const id = explicit ?? ids.alloc(STICKY_ID_PREFIXES[kw as ElementType]!, node.name);
        elements.push(
          compact({
            id,
            elementType: kw as ElementType,
            label: node.name,
            position: pos(node.coords.x, node.coords.y),
            color: node.color,
          }) as BoardElement,
        );
        register(node.name, id);
        if (node.host !== undefined) {
          if (ATTACHABLE_KINDS.has(kw)) {
            pendingAttachments.push({
              index: elements.length - 1,
              host: node.host,
              raw: line,
              lineNo,
            });
          } else {
            diag(`A ${kw} can't be pinned. Only an actor, a hotspot or a note takes (on …).`);
          }
        }
        if (node.sizeSuffix) {
          diag(`A ${kw} can't be resized. Only a note takes (size …).`);
        }
        if (node.alignSuffix) {
          diag(`A ${kw} can't be aligned. Only a note takes (align …).`);
        }
        break;
      }

      case "note": {
        const split = splitAtCoords(after);
        if (!split && after.includes("[")) {
          failed(line);
          break;
        }
        // Suffixes ONLY after the coordinates (mirrors parseSticky) — notes are free text, so
        // `(on …)`, `(color …)` or `(size 1x1)` may legitimately appear inside it. `(on …)` is
        // stripped FIRST: it is always the last suffix and its host name runs to the final `)`,
        // so it may itself contain a `(color …)`, `(size …)` or `(align …)`.
        const on = parseOn(split ? split.suffix : after);
        const al = parseAlign(on.rest);
        const sz = parseSize(al.rest);
        const idp = parseId(sz.rest);
        const col = parseColor(idp.rest);
        if (al.invalid) {
          diag(
            `Couldn't read the alignment “${al.invalid.trim()}”. Write (align left|center|right top|middle|bottom).`,
          );
        }
        if (sz.invalid) {
          diag(
            `Couldn't read the size “${sz.invalid.trim()}”. Write (size <w>x<h>) with positive numbers.`,
          );
        }
        if (idp.id !== undefined || idp.invalid) {
          diag("A note can't have an id. Only a sticky takes (id …).");
        }
        // Literal `\n` back into real line breaks (multi-line notes).
        const textPart = decodeName((split ? split.name : col.rest).trim());
        const id = ids.alloc("note", textPart || "note");
        const note: NoteElement = compact({
          id,
          elementType: "note",
          label: textPart,
          position: split ? pos(split.coords.a, split.coords.b) : pos(0, 0),
          color: col.color,
          size: sz.size,
          align: modelAlign(al.align),
        }) as NoteElement;
        elements.push(note);
        if (on.host !== undefined) {
          pendingAttachments.push({ index: elements.length - 1, host: on.host, raw: line, lineNo });
        }
        break;
      }

      case "line": {
        // Project extension (freeform drawing): `line [[x,y], [x,y], …] (closed) (dashed) (color x)`.
        const col = parseColor(after);
        const multi = parseMultiCoords(col.rest);
        if (!multi || multi.tuples.length < 2) {
          failed(line);
          break;
        }
        const onDrawing = parseOn(multi.rest);
        if (onDrawing.host !== undefined) {
          diag("A drawing can't be pinned. Only an actor, a hotspot or a note takes (on …).");
        }
        const szDrawing = parseSize(onDrawing.rest);
        if (szDrawing.size || szDrawing.invalid) {
          diag("A drawing can't be resized. Only a note takes (size …).");
        }
        const alDrawing = parseAlign(onDrawing.rest);
        if (alDrawing.align || alDrawing.invalid) {
          diag("A drawing can't be aligned. Only a note takes (align …).");
        }
        const idDrawing = parseId(onDrawing.rest);
        if (idDrawing.id !== undefined || idDrawing.invalid) {
          diag("A drawing can't have an id. Only a sticky takes (id …).");
        }
        const flags = onDrawing.rest.toLowerCase();
        const strokeStyle: DrawingStrokeStyle | undefined = flags.includes("(dashed)")
          ? "dashed"
          : flags.includes("(dotted)")
            ? "dotted"
            : undefined;
        const points = multi.tuples.map((t) => pos(t.a, t.b));
        const drawing: DrawingElement = {
          id: ids.alloc("draw", "line"),
          elementType: "drawing",
          label: "",
          position: points[0]!,
          points,
          ...(flags.includes("(closed)") ? { closed: true } : {}),
          ...(strokeStyle ? { strokeStyle } : {}),
          ...(col.color ? { color: col.color } : {}),
        };
        elements.push(drawing);
        break;
      }

      default: {
        // Unknown keyword: try as an arrow (e.g. `A -> B; async`), otherwise keep raw.
        if (!pushArrow(line)) rawPassthrough.push(line);
      }
    }
  }

  // Reference resolution for arrow endpoints and `(on …)` hosts: a `#`-prefixed token is an id
  // reference (ids exist for EVERY element, not only those declared with `(id …)`); anything
  // else stays a label lookup. No fallback across the two — `#` always means id.
  const resolve = (token: string): BoardElement | undefined => {
    if (token.startsWith("#")) {
      const id = token.slice(1);
      return elements.find((e) => e.id === id);
    }
    const id = nameToId.get(decodeName(token));
    return id !== undefined ? elements.find((e) => e.id === id) : undefined;
  };

  // Deferred like arrows so a host may be declared after its attacher. On failure the sticky
  // stays WITHOUT attachedTo (it was already created above) — no rawPassthrough, the line is
  // otherwise fully represented.
  for (const att of pendingAttachments) {
    const host = resolve(att.host);
    if (!host) {
      diag(`There's no “${att.host}” on the board to pin this to.`, att.lineNo, att.raw);
      continue;
    }
    if (!HOST_KINDS.has(host.elementType)) {
      diag(
        `“${att.host}” is a ${host.elementType}. An actor, a hotspot or a note is pinned to an event, a command, an aggregate, a policy, a read model or an external system.`,
        att.lineNo,
        att.raw,
      );
      continue;
    }
    elements[att.index] = { ...elements[att.index]!, attachedTo: host.id } as BoardElement;
  }

  let arrowN = 0;
  // An explicit `(id arrow_N)` on a sticky may occupy an arrow-id slot — skip it (diagram-js
  // has ONE id namespace for shapes and connections; validateBoard rejects the collision).
  const elementIds = new Set(elements.map((e) => e.id));
  const nextArrowId = (): string => {
    let id = `arrow_${++arrowN}`;
    while (elementIds.has(id)) id = `arrow_${++arrowN}`;
    return id;
  };
  const edges: BoardEdge[] = [];
  for (const arrow of pendingArrows) {
    const from = resolve(arrow.left);
    const to = resolve(arrow.right);
    if (!from || !to) {
      rawPassthrough.push(arrow.raw);
      diag(
        !from
          ? `This arrow starts at “${arrow.left}”, which isn't on the board.`
          : `This arrow points to “${arrow.right}”, which isn't on the board.`,
        arrow.lineNo,
        arrow.raw,
      );
      continue;
    }
    // Only reachable via `#id` refs (labels are registered for sticky kinds only) — an edge to
    // a note/drawing would make validateBoard throw, so it is dropped losslessly instead.
    const nonSticky = !STICKY_KINDS.has(from.elementType)
      ? ([arrow.left, from] as const)
      : !STICKY_KINDS.has(to.elementType)
        ? ([arrow.right, to] as const)
        : undefined;
    if (nonSticky) {
      rawPassthrough.push(arrow.raw);
      diag(
        `“${nonSticky[0]}” is a ${nonSticky[1].elementType}, and an arrow only connects stickies.`,
        arrow.lineNo,
        arrow.raw,
      );
      continue;
    }
    edges.push(
      compact({
        id: nextArrowId(),
        edgeType: "arrow",
        from: from.id,
        to: to.id,
        label: arrow.label,
      }) as BoardEdge,
    );
  }

  const board = compact({
    schemaVersion: CURRENT_SCHEMA_VERSION,
    config,
    elements,
    edges,
    rawPassthrough: rawPassthrough.length ? rawPassthrough : undefined,
  }) as EventStormingBoard;

  return { board: validateBoard(board), diagnostics };
}

interface ParsedSticky {
  readonly name: string;
  readonly coords: { x: number; y: number };
  /** Color override `(color …)` — supported on every element line. */
  readonly color?: string;
  /** Attachment `(on <Host Name>)` — host name still undecoded, resolved by the caller. */
  readonly host?: string;
  /** Explicit `(id …)` — the caller claims it (duplicate ⇒ diagnostic + fresh id). */
  readonly id?: string;
  /** The matched `(id …)` text when present but unreadable (illegal charset). */
  readonly idInvalid?: string;
  /** A `(size …)` suffix appeared — only notes support it, the caller reports a diagnostic. */
  readonly sizeSuffix?: boolean;
  /** An `(align …)` suffix appeared — only notes support it, the caller reports a diagnostic. */
  readonly alignSuffix?: boolean;
}

/**
 * Parses `<name> [x, y] [(color …)] [(id …)] [(on …)]`. The suffixes are looked up ONLY AFTER
 * the coordinates — parentheses inside the name stay untouched. `(on …)` is extracted first
 * (its host name runs to the final `)` and may itself contain a `(color …)`, `(id …)` or
 * `(size …)`). Coordinates are optional and default to `[0, 0]`; a malformed tuple (bracket
 * present but unreadable) is rejected so the caller can report a diagnostic instead of
 * swallowing it into the name.
 */
function parseSticky(after: string): ParsedSticky | null {
  const split = splitAtCoords(after);
  if (split) {
    if (!split.name) return null;
    const on = parseOn(split.suffix);
    const idp = parseId(on.rest);
    const al = parseAlign(idp.rest);
    const sz = parseSize(al.rest);
    const col = parseColor(sz.rest);
    return compact({
      name: decodeName(split.name),
      coords: { x: split.coords.a, y: split.coords.b },
      color: col.color ?? undefined,
      host: on.host,
      id: idp.id,
      idInvalid: idp.invalid,
      sizeSuffix: sz.size || sz.invalid ? true : undefined,
      alignSuffix: al.align || al.invalid ? true : undefined,
    }) as ParsedSticky;
  }
  if (after.includes("[")) return null;
  const on = parseOn(after);
  const idp = parseId(on.rest);
  const al = parseAlign(idp.rest);
  const sz = parseSize(al.rest);
  const col = parseColor(sz.rest);
  const name = col.rest.trim();
  if (!name) return null;
  return compact({
    name: decodeName(name),
    coords: { x: 0, y: 0 },
    color: col.color ?? undefined,
    host: on.host,
    id: idp.id,
    idInvalid: idp.invalid,
    sizeSuffix: sz.size || sz.invalid ? true : undefined,
    alignSuffix: al.align || al.invalid ? true : undefined,
  }) as ParsedSticky;
}
